/**
 * Orthographic front elevation for a compact semiconductor process tool.
 * All artwork stays inside width × height — load ports align to dock-edge rx.
 */
export const EQUIPMENT_2D_DESIGN = {
  width: 160,
  height: 110,
  stroke: 1.25,
  margin: 4,
  baseH: 12,
  /** Left EFEM / operator service module */
  efemW: 46,
  efemTop: 10,
  /** Main process chamber (center-right) */
  chamberX: 50,
  chamberW: 106,
  chamberTop: 12,
  chamberH: 58,
  windowX: 62,
  windowY: 22,
  windowW: 82,
  windowH: 40,
  /** Recessed HMI on EFEM */
  hmiX: 12,
  hmiY: 18,
  hmiW: 30,
  hmiH: 20,
  /** Integrated load-port deck (dock face) */
  deckY: 78,
  deckH: 20,
  portW: 26,
  portH: 12,
  portPadY: 84,
  pad: 6,
} as const;

export type Equipment2DDockEdge = 'top' | 'bottom' | 'left' | 'right';

/** Dominant dock face from a port list (tools default to the bottom / track edge). */
export function majorityDockEdge(
  ports: ReadonlyArray<{ edge?: Equipment2DDockEdge; rx?: number; ry?: number }>
): Equipment2DDockEdge {
  if (ports.length === 0) return 'bottom';
  const counts: Record<Equipment2DDockEdge, number> = {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  };
  for (const port of ports) {
    const edge =
      port.edge ??
      (port.ry === 0 ? 'top' : port.ry === 1 ? 'bottom' : port.rx === 0 ? 'left' : port.rx === 1 ? 'right' : 'bottom');
    counts[edge] += 1;
  }
  return (Object.keys(counts) as Equipment2DDockEdge[]).reduce(
    (best, candidate) => (counts[candidate] > counts[best] ? candidate : best),
    'bottom'
  );
}

export function getEquipment2DLayout(
  width: number,
  height: number,
  dockEdge: Equipment2DDockEdge = 'bottom'
) {
  const aspect = EQUIPMENT_2D_DESIGN.width / EQUIPMENT_2D_DESIGN.height;
  let cw = width;
  let ch = cw / aspect;
  if (ch > height) {
    ch = height;
    cw = ch * aspect;
  }
  const ox =
    dockEdge === 'left' ? 0 : dockEdge === 'right' ? width - cw : (width - cw) / 2;
  const oy =
    dockEdge === 'top' ? 0 : dockEdge === 'bottom' ? height - ch : (height - ch) / 2;
  return {
    s: cw / EQUIPMENT_2D_DESIGN.width,
    ox,
    oy,
    cw,
    ch,
  };
}

export function resolveEquipmentPortCount(
  kind: 'stb' | 'ohb' | 'lifter' | 'rectangle' | 'equipment',
  override?: number
): number {
  if (typeof override === 'number' && Number.isFinite(override) && override > 0) {
    return Math.max(1, Math.min(6, Math.round(override)));
  }
  if (kind === 'lifter') return 1;
  if (kind === 'stb' || kind === 'ohb') return 2;
  return 2;
}

export function defaultDockFractions(count: number): number[] {
  const n = Math.max(1, Math.round(count));
  if (n <= 1) return [0.5];
  if (n === 4) return [0.2, 0.4, 0.6, 0.8];
  return Array.from({ length: n }, (_, i) => (i + 1) / (n + 1));
}

/** Map layout rx (full element width) to schematic design-space X. */
export function loadPortDesignX(rx: number, layoutWidth: number, ox: number, scale: number): number {
  return (rx * layoutWidth - ox) / scale;
}

export function getEquipmentLoadPortAnchors(
  width: number,
  height: number,
  count?: number,
  dockEdge: Equipment2DDockEdge = 'bottom'
): Array<{ rx: number; ry: number }> {
  void width;
  void height;
  const n = resolveEquipmentPortCount('equipment', count);
  const fractions = defaultDockFractions(n);
  return fractions.map((f) => {
    switch (dockEdge) {
      case 'top':
        return { rx: f, ry: 0 };
      case 'left':
        return { rx: 0, ry: f };
      case 'right':
        return { rx: 1, ry: f };
      case 'bottom':
      default:
        return { rx: f, ry: 1 };
    }
  });
}
